import React from "react";
import { Card, Tag, Typography, Space, Button, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { confirmar } from "../../../../ui/confirmar";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../revi/FilaTocable";
import { CardActionsMenu } from "../CardActionsMenu";
import "./EvaluationItemCard.scss";

const { Text, Paragraph } = Typography;

// Misma visibilidad que la ficha de terapia, mismas claves: si el dia de manana
// alguien cambia "Privada" por otra palabra, cambia en las dos a la vez.
function visibilityLabel(t, v) {
  if (v === "global") return { text: t("visibilidad.global"), color: "purple" };
  if (v === "public")
    return { text: t("visibilidad.institucion"), color: "blue" };
  return { text: t("visibilidad.privada"), color: "default" };
}

function getQuestionsCount(evaluation) {
  // ✅ Nuevo campo backend
  if (Number.isFinite(evaluation?.questionsCount))
    return evaluation.questionsCount;

  // ✅ Fallback si el backend sigue enviando questions array
  if (Array.isArray(evaluation?.questions)) return evaluation.questions.length;

  return 0;
}

export function EvaluationItemCard({
  evaluation,
  mode = "standard", // "standard" | "my" | "shared"
  onView,
  onEdit,
  onDelete,
  onQuickApply,
}) {
  // El titulo y la descripcion de la evaluacion son contenido: los escribe el
  // terapeuta o vienen de una escala validada. Solo se traduce el respaldo de
  // cuando no hay ninguno.
  const { t } = useTranslation(["ui", "common"]);

  const title = evaluation?.title || t("evaluaciones.ficha.sinTitulo");
  const desc = evaluation?.description || "";
  const isStandard = evaluation?.type === "standardized";
  const v = visibilityLabel(t, evaluation?.visibility);

  const questionsCount = getQuestionsCount(evaluation);

  const movil = useMovil();

  /**
   * En movil, tarjeta-fila. La cabecera con su barra de tres iconos, la
   * descripcion a dos lineas, el boton de aplicar y cuatro etiquetas suman mas
   * de 250 px por escala.
   *
   * Se queda lo que decide: el nombre del instrumento, si es estandar o propia,
   * y la accion que de verdad se usa desde un movil, que es pasarsela al
   * paciente que tienes delante. Consultar el detalle es tocar la fila; editar
   * y borrar viven en el menu, porque redactar una escala de 39 items no se
   * hace con el pulgar.
   */
  if (movil) {
    const acciones = [
      {
        key: "ver",
        icon: <EyeOutlined />,
        label: t("common:actions.view"),
        run: onView,
      },
    ];

    if (mode === "my") {
      acciones.push({
        key: "edit",
        icon: <EditOutlined />,
        label: t("common:actions.edit"),
        run: onEdit,
      });
      acciones.push({
        key: "del",
        icon: <DeleteOutlined />,
        label: t("common:actions.delete"),
        peligroso: true,
        run: onDelete,
        confirmacion: {
          titulo: t("common:confirm.deleteTitle"),
          consecuencia: title,
          textoOk: t("common:confirm.yes"),
        },
      });
    }

    return (
      <FilaTocable
        as={Card}
        alto={onQuickApply ? 144 : 88}
        className="revi-eval-card revi-eval-card--fila"
        onTocar={onView}
        aria-label={title}
        hoverable={false}
        chevron={false}
        kebab={<CardActionsMenu ariaLabel={title} items={acciones} />}
      >
        <div className="revi-eval-card__filaTexto">
          <span className="revi-eval-card__filaTitulo">{title}</span>

          <div className="revi-eval-card__filaTags">
            {isStandard ? (
              <Tag color="purple">{t("evaluaciones.ficha.estandar")}</Tag>
            ) : (
              <Tag color="blue">{t("evaluaciones.ficha.personalizada")}</Tag>
            )}
            {evaluation?.clinicalArea ? (
              <Tag>{evaluation.clinicalArea}</Tag>
            ) : null}
          </div>

          {onQuickApply ? (
            <Button
              type="primary"
              ghost
              block
              icon={<PlayCircleOutlined />}
              className="revi-eval-card__filaAplicar"
              onClick={(e) => {
                e.stopPropagation();
                onQuickApply(evaluation);
              }}
            >
              {t("evaluaciones.ficha.aplicarTest")}
            </Button>
          ) : null}
        </div>
      </FilaTocable>
    );
  }

  return (
    <Card
      hoverable
      className="revi-eval-card"
      onClick={onView}
      title={
        <Space size={8} className="revi-eval-card__title">
          <FileTextOutlined />
          <Text strong ellipsis={{ tooltip: title }}>
            {title}
          </Text>
        </Space>
      }
      extra={
        <Space size={6} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t("common:actions.view")}>
            <Button size="small" icon={<EyeOutlined />} onClick={onView} />
          </Tooltip>

          {mode === "my" ? (
            <>
              <Tooltip title={t("common:actions.edit")}>
                <Button size="small" icon={<EditOutlined />} onClick={onEdit} />
              </Tooltip>
              <Tooltip title={t("common:actions.delete")}>
                {/*
                  Borrar una evaluacion era un solo toque, sin confirmar y sin
                  deshacer, y el mensaje posterior decia que todo habia ido
                  bien. Es la unica accion de esta tarjeta que destruye trabajo,
                  asi que pregunta nombrando cual.
                */}
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    confirmar({
                      titulo: t("common:confirm.deleteTitle"),
                      consecuencia: title,
                      textoOk: t("common:confirm.yes"),
                      onOk: onDelete,
                    })
                  }
                />
              </Tooltip>
            </>
          ) : null}
        </Space>
      }
    >
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Paragraph
          type="secondary"
          className="revi-eval-card__desc"
          ellipsis={{ rows: 2, tooltip: desc }}
        >
          {desc || "—"}
        </Paragraph>

        {onQuickApply ? (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<PlayCircleOutlined />}
            className="revi-eval-card__quick-apply"
            onClick={(e) => {
              e.stopPropagation();
              onQuickApply(evaluation);
            }}
          >
            {t("evaluaciones.ficha.aplicarTest")}
          </Button>
        ) : null}

        <Space wrap size={6} className="revi-eval-card__tags">
          {isStandard ? (
            <Tag color="purple">{t("evaluaciones.ficha.estandar")}</Tag>
          ) : (
            <Tag color="blue">{t("evaluaciones.ficha.personalizada")}</Tag>
          )}

          {!isStandard ? <Tag color={v.color}>{v.text}</Tag> : null}

          {evaluation?.clinicalArea ? (
            <Tag>{evaluation.clinicalArea}</Tag>
          ) : null}
          {evaluation?.subtype ? <Tag>{evaluation.subtype}</Tag> : null}

          {/*
            Contador: con una pregunta decia "1 preguntas".
          */}
          <Tag>
            {t("common:contadores.preguntas", { count: questionsCount })}
          </Tag>
        </Space>
      </Space>
    </Card>
  );
}
